import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ShieldCheck, Plus, Trash2, XCircle, CheckCircle2, Clock, AlertCircle, Ban } from "lucide-react";

const STATUS_MAP = {
  pending:   { label: "Em análise",   cls: "bg-amber-100 text-amber-800",       Icon: Clock },
  active:    { label: "Ativo",        cls: "bg-emerald-100 text-emerald-800",   Icon: CheckCircle2 },
  suspended: { label: "Suspenso",     cls: "bg-slate-200 text-slate-700",       Icon: Ban },
  expired:   { label: "Expirado",     cls: "bg-red-100 text-red-800",           Icon: XCircle },
  rejected:  { label: "Rejeitado",    cls: "bg-red-100 text-red-800",           Icon: XCircle },
};

const CredentialFormModal = ({ open, onClose, onSaved }) => {
  const [insurers, setInsurers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    insurer_id: "", plan_id: "", credential_number: "", specialty: "",
    teleconsultation_allowed: true, valid_from: "", valid_until: "",
    document_url: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/convenios/insurers").then(r => setInsurers(r.data)).catch(() => {});
    setForm({
      insurer_id: "", plan_id: "", credential_number: "", specialty: "",
      teleconsultation_allowed: true, valid_from: "", valid_until: "",
      document_url: "", notes: "",
    });
  }, [open]);

  useEffect(() => {
    if (!form.insurer_id) { setPlans([]); return; }
    api.get(`/convenios/insurers/${form.insurer_id}/plans`).then(r => setPlans(r.data)).catch(() => {});
  }, [form.insurer_id]);

  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.insurer_id) return toast.error("Selecione a operadora");
    if (!form.credential_number.trim()) return toast.error("Número do credenciamento obrigatório");
    if (!form.specialty.trim()) return toast.error("Especialidade obrigatória");
    setBusy(true);
    try {
      await api.post("/convenios/me/credentials", {
        insurer_id: form.insurer_id,
        plan_id: form.plan_id || null,
        credential_number: form.credential_number.trim(),
        specialty: form.specialty.trim(),
        teleconsultation_allowed: form.teleconsultation_allowed,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        document_url: form.document_url || null,
        notes: form.notes || null,
      });
      toast.success("Credenciamento enviado — aguardando aprovação do admin.");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cred-form-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0B2B62]">
            <ShieldCheck className="w-5 h-5" />
            <div className="font-display font-bold text-lg">Adicionar credenciamento</div>
          </div>
          <button onClick={onClose}><XCircle className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Operadora *</span>
            <select value={form.insurer_id} onChange={e => set("insurer_id", e.target.value)}
              data-testid="cred-form-insurer"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">— Selecione —</option>
              {insurers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Plano (opcional)</span>
            <select value={form.plan_id} onChange={e => set("plan_id", e.target.value)}
              disabled={!plans.length}
              data-testid="cred-form-plan"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100">
              <option value="">— Todos os planos —</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Nº credencial *</span>
              <input value={form.credential_number} onChange={e => set("credential_number", e.target.value)}
                data-testid="cred-form-number"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Especialidade *</span>
              <input value={form.specialty} onChange={e => set("specialty", e.target.value)}
                placeholder="Psicologia, Nutrição…"
                data-testid="cred-form-specialty"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Válido de</span>
              <input type="date" value={form.valid_from} onChange={e => set("valid_from", e.target.value)}
                data-testid="cred-form-valid-from"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">Válido até</span>
              <input type="date" value={form.valid_until} onChange={e => set("valid_until", e.target.value)}
                data-testid="cred-form-valid-until"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-600 font-semibold uppercase tracking-widest">URL do documento comprobatório</span>
            <input value={form.document_url} onChange={e => set("document_url", e.target.value)}
              placeholder="https://drive.google.com/…"
              data-testid="cred-form-doc"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
            <input type="checkbox" checked={form.teleconsultation_allowed}
              onChange={e => set("teleconsultation_allowed", e.target.checked)}
              data-testid="cred-form-tele" className="rounded border-slate-300" />
            Teleconsulta autorizada pela operadora
          </label>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>O credenciamento entra como <b>Em análise</b>. O admin da RAF verifica e libera. Você só aparece como "aceita esse convênio" depois da aprovação.</span>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy}
            data-testid="cred-form-save"
            className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Enviando…" : "Enviar credenciamento"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfessionalConveniosSection = () => {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/convenios/me/credentials");
      setCreds(data);
    } catch (e) { /* ignore silently */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Remover credenciamento em ${c.insurer?.name}?\n\nSó é permitido enquanto o status ainda é Em análise.`)) return;
    try {
      await api.delete(`/convenios/me/credentials/${c.id}`);
      toast.success("Credenciamento removido");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="pro-convenios-section">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h2 className="font-display font-bold text-lg text-[#0B2B62]">Meus convênios</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Adicione seu credenciamento em cada operadora que aceita teleconsulta. Você só aparece como "aceita esse convênio" na vitrine quando o status estiver <b>Ativo</b>.
          </p>
        </div>
        <button onClick={() => setCreating(true)}
          data-testid="pro-cred-new"
          className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Adicionar credenciamento
        </button>
      </div>
      {loading ? <div className="text-slate-500 text-sm">Carregando…</div>
      : creds.length === 0 ? (
        <div className="p-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 text-slate-500 text-sm text-center">
          Você ainda não cadastrou nenhum credenciamento. Toque em <b>Adicionar credenciamento</b> pra começar.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {creds.map(c => {
            const s = STATUS_MAP[c.status] || STATUS_MAP.pending;
            return (
              <div key={c.id} data-testid={`pro-cred-${c.id}`} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#0B2B62]">{c.insurer?.name || "?"}</div>
                    <div className="text-xs text-slate-600">{c.specialty}</div>
                    {c.plan?.name && <div className="text-[11px] text-slate-500">Plano: {c.plan.name}</div>}
                    <div className="text-[11px] text-slate-500 mt-1 font-mono">{c.credential_number}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${s.cls}`}>
                    <s.Icon className="w-3 h-3" /> {s.label}
                  </span>
                </div>
                {c.status === "rejected" && c.rejection_reason && (
                  <div className="mt-2 p-2 rounded-md bg-red-50 text-red-800 text-xs italic">"{c.rejection_reason}"</div>
                )}
                {c.status === "pending" && (
                  <button onClick={() => remove(c)}
                    data-testid={`pro-cred-remove-${c.id}`}
                    className="mt-3 text-xs px-2.5 py-1 text-red-600 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Remover
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <CredentialFormModal open={creating} onClose={() => setCreating(false)} onSaved={load} />
    </section>
  );
};

export default ProfessionalConveniosSection;
